import React from "react"
import { I18nextProvider } from "react-i18next"
import i18n, { applyPreferredLanguage } from "./src/i18n"

const THEME_QUERY_PARAM = "theme"
const FORCED_THEMES = new Set(["light", "dark"])
const VIEWPORT_HEIGHT_CSS_VAR = "--app-dvh"

const applyPreferredTheme = search => {
  if (typeof document === "undefined") return

  const params = new URLSearchParams(search || "")
  const theme = (params.get(THEME_QUERY_PARAM) || "").toLowerCase()

  if (FORCED_THEMES.has(theme)) {
    document.documentElement.setAttribute("data-theme", theme)
    return
  }

  document.documentElement.removeAttribute("data-theme")
}

const updateViewportHeightVar = () => {
  if (typeof document === "undefined" || typeof window === "undefined") return

  const viewportHeight = window.visualViewport?.height || window.innerHeight
  if (!viewportHeight) return

  const roundedHeight = Math.round(viewportHeight * 100) / 100
  document.documentElement.style.setProperty(
    VIEWPORT_HEIGHT_CSS_VAR,
    `${roundedHeight}px`
  )
}

const initViewportHeightVar = () => {
  if (typeof window === "undefined") return

  let rafId = null
  const onViewportChange = () => {
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId)
    }

    rafId = window.requestAnimationFrame(() => {
      updateViewportHeightVar()
      rafId = null
    })
  }

  updateViewportHeightVar()
  window.addEventListener("resize", onViewportChange, { passive: true })
  window.addEventListener("orientationchange", onViewportChange, {
    passive: true,
  })

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", onViewportChange, {
      passive: true,
    })
    window.visualViewport.addEventListener("scroll", onViewportChange, {
      passive: true,
    })
  }
}

export const onClientEntry = () => {
  applyPreferredLanguage()
  applyPreferredTheme(window.location.search)
  initViewportHeightVar()
}

export const onRouteUpdate = ({ location }) => {
  applyPreferredTheme(location?.search || window.location.search)
  updateViewportHeightVar()
}

export const wrapRootElement = ({ element }) => (
  <I18nextProvider i18n={i18n}>{element}</I18nextProvider>
)
